import { Modal, ModalHeader, ModalBody, ModalFooter, ModalOverlay, ModalContent, Text, HStack, VStack, Box } from '@chakra-ui/react'
import { MouseEventHandler } from 'react';
import ButtonComponent from './form/Button';
import TextInput from './form/TextInput';
import { Formik } from 'formik';
import * as Yup from "yup";

interface ConfirmModalProps {
    isOpen: boolean;
    onConfirm?: Function | any;
    onConfirmThird?: any;
    onClose?: () => void;
    header?: string | any;
    message?: string | number;
    subMessage?: string | number;
    loading?: boolean;
    yesLabel?: string;
    noLabel?: string;
    thirdLabel?: string;
    withForm?: boolean;
    requireOtp?: boolean;
    formData?: (props: any) => React.ReactNode;
    handleSave?: (values: any, setSubmitting: any) => Promise<void> | void;
    keepOpenOnConfirm?: boolean;
    isDestructive?: boolean;
}

function ConfirmModal({
    isOpen,
    onConfirm,
    onClose,
    header,
    message,
    subMessage,
    loading,
    noLabel,
    yesLabel,
    thirdLabel,
    onConfirmThird,
    withForm,
    requireOtp = false,
    formData,
    handleSave,
    keepOpenOnConfirm,
    isDestructive
}: ConfirmModalProps) {

    const handleYes: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation()
        if (!keepOpenOnConfirm) onClose && onClose()
        onConfirm()
    }
    const handleNo: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation()
        onClose && onClose()
    }

    const handleThird: MouseEventHandler<HTMLButtonElement> = (e) => {
        e.stopPropagation()
        if (!keepOpenOnConfirm) onClose && onClose()
        onConfirmThird()
    }

  const schema = Yup.object().shape(requireOtp ? {
    otp_code: Yup.string()
      .required("Authenticator code is required")
      .matches(/^\d{6}$/, "Enter a six-digit Authenticator code"),
  } : {});

    return (
        <Modal isOpen={isOpen} onClose={() => onClose && onClose()} size='md' isCentered>
            <ModalOverlay backdropFilter='blur(8px)' />
            <ModalContent w='sm' maxW='90vw'>
                <ModalHeader>{header}</ModalHeader>
                <ModalBody textAlign='center'>
                    <Box w='full' mb={4}>
                        <Text
                            fontSize='md'
                            fontWeight={500}
                        >
                            {message ?? 'Are you sure ?'}
                        </Text>
                        {subMessage &&
                            <Text
                                padding='10px'
                                color={'gray'}
                                fontSize='md'
                            >
                                {subMessage}
                            </Text>
                        }
                    </Box>
                    {(withForm || requireOtp) &&
                        <Formik
                            initialValues={requireOtp ? { otp_code: "" } : {}}
                            validationSchema={schema}
                             onSubmit={(values, { setSubmitting }) => {
                                handleSave?.(values, setSubmitting);
                            }}
                        >
                            {({
                                values,
                                touched,
                                errors,
                                isSubmitting,
                                handleChange,
                                setFieldValue,
                                handleSubmit,
                            }) => (
                            <>
                                <Box w='full'>
                                    {requireOtp && (
                                        <Box
                                            mb={4}
                                            textAlign="left"
                                        >
                                            <TextInput
                                                id="confirmation-otp"
                                                label="OTP Code"
                                                aria-label="OTP Code"
                                                name="otp_code"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                                maxLength={6}
                                                value={values.otp_code || ""}
                                                disabled={isSubmitting}
                                                error={touched.otp_code ? errors.otp_code : undefined}
                                                onChange={(event) => setFieldValue(
                                                    "otp_code",
                                                    event.target.value.replace(/\D/g, "").slice(0, 6)
                                                )}
                                            />
                                            <Text fontSize="sm" mb={2}>
                                                Enter the six-digit code from your Authenticator app.
                                            </Text>
                                        </Box>
                                    )}

                                    {formData?.({
                                        values,
                                        touched,
                                        errors,
                                        handleChange,
                                        setFieldValue,
                                    })}

                                    <HStack mt={8} justifyContent='space-between' w='100%'>
                                        <ButtonComponent
                                            label={noLabel ?? 'Cancel'}
                                            variant='outline'
                                            bgColor={'red'}
                                            onClick={handleNo}
                                            w='100%'
                                            size='md'
                                        />

                                        <ButtonComponent
                                            label={yesLabel ?? "Proceed"}
                                            colorScheme="facebook"
                                            w="100%"
                                            size='md'
                                            variant='solid'
                                            isLoading={isSubmitting}
                                            onClick={() => handleSubmit()}
                                            isDisabled={isSubmitting || (requireOtp && !/^\d{6}$/.test(values.otp_code || ""))}
                                        />
                                    </HStack>
                                </Box>
                            </>
                            )}
                        </Formik>
                    }
                </ModalBody>
                {!(withForm || requireOtp) &&
                    <ModalFooter w={'100%'}>
                        <VStack spacing={4} w='100%'>
                            <HStack justifyContent='space-between' w='100%'>
                                <ButtonComponent
                                    label={noLabel ?? 'No'}
                                    variant='outline'
                                    bgColor={isDestructive ? 'transparent' : 'red'}
                                    color={isDestructive ? '#1A0044' : undefined}
                                    borderColor={isDestructive ? '#CBD5E0' : undefined}
                                    onClick={handleNo}
                                    w='100%'
                                    size='md'
                                />
                                <ButtonComponent
                                    label={yesLabel ?? "Yes"}
                                    onClick={handleYes}
                                    colorScheme={isDestructive ? 'red' : 'facebook'}
                                    bgColor={isDestructive ? '#E53E3E' : undefined}
                                    w="100%"
                                    size='md'
                                    variant='solid'
                                    isLoading={loading}
                                />
                            </HStack>
                            {thirdLabel &&
                                <ButtonComponent
                                    label={thirdLabel}
                                    variant='outline'
                                    bgColor={'red.800'}
                                    onClick={handleThird}
                                    w='100%'
                                    size='md'
                                />
                            }
                        </VStack>
                    </ModalFooter>
                }
            </ModalContent>
        </Modal>
    );

}

export default ConfirmModal
